import { Calendar, Video, FileText, User, PlayCircle, Loader } from 'lucide-react';
import { useState, useEffect } from 'react';

const PastWebinars = () => {
  const [pastWebinars, setPastWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPastWebinars = async () => {
      const cachedData = localStorage.getItem("cachedPastWebinars_v2");
      const cacheTimestamp = localStorage.getItem("pastWebinarsTimestamp_v2");
      const now = Date.now();

      if (cachedData && cacheTimestamp && now - parseInt(cacheTimestamp) < 3600000) {
        setPastWebinars(JSON.parse(cachedData));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch("https://admin.torchbearer.co.ke/api/training-programs/webinars/past");

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          const parsedPrograms = data.data.map(program => {
            let parsedFeatures = [];
            if (program.features) {
              try {
                parsedFeatures = typeof program.features === 'string'
                  ? JSON.parse(program.features)
                  : Array.isArray(program.features)
                    ? program.features
                    : [];
              } catch (e) {
                console.error(`Failed to parse features for program ${program.id}:`, e);
                parsedFeatures = [];
              }
            }
            return {
              ...program,
              features: parsedFeatures,
            };
          });

          setPastWebinars(parsedPrograms);
          localStorage.setItem("cachedPastWebinars_v2", JSON.stringify(parsedPrograms));
          localStorage.setItem("pastWebinarsTimestamp_v2", now.toString());
        } else {
          throw new Error(data.message || "Failed to fetch webinars");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPastWebinars();
  }, []);

  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return 'Date TBA';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : start;
    const options = { month: 'short', day: 'numeric', year: 'numeric' };

    if (start.toDateString() === end.toDateString()) {
      return start.toLocaleDateString('en-US', options);
    }
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.getDate()}, ${start.getFullYear()}`;
    }
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  };

  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const getYoutubeThumbnail = (url) => {
    const videoId = getYoutubeVideoId(url);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-4">
          <div className="w-2 h-8 bg-amber-500 rounded-full"></div>
          Past Webinar Archive
        </h3>
        <div className="text-center py-20 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
          <Loader className="w-12 h-12 animate-spin mx-auto text-amber-500 mb-4" />
          <p className="text-slate-500 font-bold">Synchronizing records...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 px-4">
        <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-4">
          <div className="w-2 h-8 bg-amber-500 rounded-full"></div>
          Past Webinar Archive
        </h3>
        <div className="bg-red-50 border-2 border-red-100 rounded-3xl p-12 text-center max-w-2xl mx-auto">
          <p className="text-red-900 text-xl font-bold mb-3">Sync Error</p>
          <p className="text-red-600 mb-8">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-red-700 transition-all"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-20 md:pt-48 md:pb-32 px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-12">
        <h3 className="text-3xl font-black text-gray-900 flex items-center gap-4">
          <div className="w-2 h-8 bg-amber-500 rounded-full"></div>
          Past Webinar Archive
        </h3>
        <span className="text-xs font-bold uppercase tracking-widest bg-amber-100 text-amber-700 px-4 py-1.5 rounded-full">
          {pastWebinars.length} Recordings
        </span>
      </div>

      {pastWebinars.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-100 p-20 text-center shadow-sm">
          <Video className="w-16 h-16 text-slate-200 mx-auto mb-6" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">Archive empty</h3>
          <p className="text-slate-500 max-w-xs mx-auto">
            Check back later for recordings of our professional webinars.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {pastWebinars.map((webinar) => {
            const youtubeVideoId = getYoutubeVideoId(webinar.recording_url);
            const displayImage = getYoutubeThumbnail(webinar.recording_url) || webinar.image_url || '/images/default-training-past.png';

            return (
              <div key={webinar.id} className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-amber-400 transition-all duration-300 hover:shadow-xl hover:shadow-amber-50/50 flex flex-col">
                {/* Media Preview Section */}
                <div className="relative aspect-video overflow-hidden bg-slate-900">
                  {youtubeVideoId ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${youtubeVideoId}`}
                      title={webinar.title}
                      className="w-full h-full"
                      allowFullScreen
                    />
                  ) : (
                    <>
                      <img
                        src={displayImage}
                        alt={webinar.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
                      {!webinar.recording_url && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="bg-slate-800/80 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-xs font-bold">
                            No Recording
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm text-slate-900 px-3 py-1.5 rounded-lg font-black text-[10px] shadow-lg flex items-center gap-1.5">
                    <Calendar className="w-3 h-3 text-amber-600" />
                    {formatDateRange(webinar.start_date, webinar.end_date)}
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-6 flex-grow flex flex-col">
                  <h3 className="text-lg font-black text-slate-900 mb-3 line-clamp-2 leading-tight group-hover:text-amber-600 transition-colors h-12">
                    {webinar.title}
                  </h3>

                  {webinar.speaker && (
                    <div className="flex items-center gap-2 mb-4 bg-slate-50 w-fit px-3 py-1.5 rounded-xl border border-slate-100">
                      <User className="w-3.5 h-3.5 text-amber-500" />
                      <span className="text-[11px] font-black text-slate-700 truncate max-w-[150px] uppercase tracking-tight">{webinar.speaker}</span>
                    </div>
                  )}

                  <p className="text-slate-500 text-xs mb-6 line-clamp-2 leading-relaxed h-8">
                    {webinar.description || "In-depth webinar coverage of specialized industry modules."}
                  </p>

                  {webinar.features && webinar.features.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-6">
                      {webinar.features.slice(0, 2).map((feature, i) => (
                        <span key={i} className="text-[9px] bg-amber-50 text-amber-700 px-2 py-1 rounded-md font-black border border-amber-100 uppercase tracking-tighter">
                          {feature}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-auto flex gap-3 pt-5 border-t border-slate-50">
                    {webinar.recording_url && !youtubeVideoId && (
                      <a
                        href={webinar.recording_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 bg-slate-900 text-white px-4 py-3 rounded-xl text-xs font-black hover:bg-amber-600 transition-all active:scale-95 shadow-md"
                      >
                        <PlayCircle className="w-4 h-4" />
                        Watch
                      </a>
                    )}
                    {webinar.slides_url && (
                      <a
                        href={webinar.slides_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${webinar.recording_url && !youtubeVideoId ? 'flex-1' : 'w-full'} flex items-center justify-center gap-2 bg-white border-2 border-slate-900 text-slate-900 px-4 py-3 rounded-xl text-xs font-black hover:bg-slate-50 transition-all active:scale-95`}
                      >
                        <FileText className="w-4 h-4" />
                        Slides
                      </a>
                    )}
                    {!webinar.slides_url && !webinar.recording_url && (
                      <div className="w-full text-center py-2.5 text-slate-400 text-[11px] font-black uppercase tracking-widest border border-dashed border-slate-200 rounded-xl">
                        Archived
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PastWebinars;